import React, { useState, useMemo } from 'react';
import { MenuProduct } from '../services/MenuService.ts';
import './MenuAuditTable.css';

interface MenuAuditTableProps {
  products: MenuProduct[];
  storeName: string;
  menuType: string;
}

type SortKey = 'name' | 'brand' | 'category' | 'subcategory' | 'thc' | 'cbd' | 'price' | 'quantity' | 'strainType';
type SortDirection = 'asc' | 'desc';

const MenuAuditTable: React.FC<MenuAuditTableProps> = ({ products, storeName, menuType }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;

    const query = searchQuery.toLowerCase();
    return products.filter(product => {
      return (
        product.name?.toLowerCase().includes(query) ||
        product.brand?.name?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query) ||
        product.subcategory?.toLowerCase().includes(query) ||
        product.id?.toLowerCase().includes(query) ||
        product.posMetaData?.sku?.toLowerCase().includes(query) ||
        product.strainType?.toLowerCase().includes(query)
      );
    });
  }, [products, searchQuery]);

  // Sort products
  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts];

    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortKey) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'brand':
          aValue = a.brand?.name || '';
          bValue = b.brand?.name || '';
          break;
        case 'category':
          aValue = a.category || '';
          bValue = b.category || '';
          break;
        case 'subcategory':
          aValue = a.subcategory || '';
          bValue = b.subcategory || '';
          break;
        case 'strainType':
          aValue = a.strainType || '';
          bValue = b.strainType || '';
          break;
        case 'thc':
          aValue = parseFloat(a.potencyThc?.formatted || '0');
          bValue = parseFloat(b.potencyThc?.formatted || '0');
          break;
        case 'cbd':
          aValue = parseFloat(a.potencyCbd?.formatted || '0');
          bValue = parseFloat(b.potencyCbd?.formatted || '0');
          break;
        case 'price':
          // Get the lowest price from variants
          aValue = Math.min(...(a.variants?.map(v => v.priceRec || v.priceMed || Infinity) || [Infinity]));
          bValue = Math.min(...(b.variants?.map(v => v.priceRec || v.priceMed || Infinity) || [Infinity]));
          break;
        case 'quantity':
          // Get total quantity from variants
          aValue = a.variants?.reduce((sum, v) => sum + (v.quantity || 0), 0) || 0;
          bValue = b.variants?.reduce((sum, v) => sum + (v.quantity || 0), 0) || 0;
          break;
        default:
          aValue = '';
          bValue = '';
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  }, [filteredProducts, sortKey, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };


  const getSortIcon = (key: SortKey) => {
    if (sortKey !== key) return '⇅';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="menu-audit-container">
      <div className="menu-audit-header">
        <div className="menu-audit-title">
          <h2>Menu Audit: {storeName}</h2>
          <span className="menu-type-badge">{menuType}</span>
        </div>
        <div className="menu-audit-stats">
          <span className="stat-item">
            <strong>Total Products:</strong> {products.length}
          </span>
          <span className="stat-item">
            <strong>Filtered:</strong> {sortedProducts.length}
          </span>
        </div>
      </div>

      <div className="menu-audit-controls">
        <input
          type="text"
          className="search-input"
          placeholder="Search by name, brand, category, SKU, product ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="table-container">
        <table className="menu-audit-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort('name')}>
                Product Name {getSortIcon('name')}
              </th>
              <th className="sortable" onClick={() => handleSort('brand')}>
                Brand {getSortIcon('brand')}
              </th>
              <th className="sortable" onClick={() => handleSort('category')}>
                Category {getSortIcon('category')}
              </th>
              <th className="sortable" onClick={() => handleSort('subcategory')}>
                Subcategory {getSortIcon('subcategory')}
              </th>
              <th className="sortable" onClick={() => handleSort('strainType')}>
                Strain {getSortIcon('strainType')}
              </th>
              <th className="sortable" onClick={() => handleSort('thc')}>
                THC% {getSortIcon('thc')}
              </th>
              <th className="sortable" onClick={() => handleSort('cbd')}>
                CBD% {getSortIcon('cbd')}
              </th>
              <th className="sortable" onClick={() => handleSort('price')}>
                Price {getSortIcon('price')}
              </th>
              <th className="sortable" onClick={() => handleSort('quantity')}>
                Stock {getSortIcon('quantity')}
              </th>
              <th>Product ID</th>
            </tr>
          </thead>
          <tbody>
            {sortedProducts.map((product) => (
                <tr key={product.id} className="product-row">
                  <td className="product-name">{product.name}</td>
                  <td>{product.brand?.name || 'N/A'}</td>
                  <td>{product.category || 'N/A'}</td>
                  <td>{product.subcategory || 'N/A'}</td>
                  <td>{product.strainType || 'N/A'}</td>
                  <td>{product.potencyThc?.formatted || 'N/A'}</td>
                  <td>{product.potencyCbd?.formatted || 'N/A'}</td>
                  <td>
                    {product.variants && product.variants.length > 0
                      ? `$${Math.min(...product.variants.map(v => v.priceRec || v.priceMed || Infinity)).toFixed(2)}`
                      : 'N/A'}
                  </td>
                  <td>
                    {product.variants?.reduce((sum, v) => sum + (v.quantity || 0), 0) || 0}
                  </td>
                  <td className="product-id">{product.id}</td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedProducts.length === 0 && (
        <div className="no-results">
          <p>No products found matching your search criteria.</p>
        </div>
      )}
    </div>
  );
};

export default MenuAuditTable;
